import React, { useState, useEffect } from 'react'
import { toastError, toastSuccess, toastLoading, toastDismiss } from '../../utils/toastHelper'
import { useTheme } from '../../hooks/useTheme'
import { useApiClient } from '../../hooks/useApiClient'
import { useQueryClient } from '@tanstack/react-query'

import type { Reserva } from '../../hooks/useReservationsAdmin'

interface EditarClienteReservaModalProps {
  isOpen: boolean
  reserva: Reserva | null
  onClose: () => void
  onSuccess: () => void
  reservaId?: string
}

const EditarClienteReservaModal: React.FC<EditarClienteReservaModalProps> = ({
  isOpen,
  reserva,
  onClose,
  onSuccess,
  reservaId,
}) => {
  const { colors } = useTheme()
  const { apiClient } = useApiClient()
  const queryClient = useQueryClient()
  const [isLoading, setIsLoading] = useState(false)
  const [nombre, setNombre] = useState('')
  const [email, setEmail] = useState('')
  const [telefono, setTelefono] = useState('')
  const [numero_documento, setNumeroDocumento] = useState('')
  const [tipo_documento, setTipoDocumento] = useState('RUT')

  useEffect(() => {
    if (reserva) {
      setNombre(reserva.cliente_nombre || reserva.huesped_nombre || '')
      setEmail(reserva.cliente_email || reserva.huesped_email || '')
      setTelefono(reserva.cliente_telefono || '')
      setNumeroDocumento(reserva.cliente_documento || '')

      // Normalizar el tipo_documento para que coincida con las opciones del select
      const tipoDocFromDB = reserva.tipo_documento || reserva.cliente_tipo_documento
      if (tipoDocFromDB && ['RUT', 'Pasaporte', 'Cédula', 'Otro'].includes(tipoDocFromDB)) {
        setTipoDocumento(tipoDocFromDB)
      } else {
        setTipoDocumento('RUT') // Default si no coincide
      }
    }
  }, [reserva, isOpen])

  if (!isOpen || !reserva) return null

  const handleGuardar = async () => {
    if (!nombre.trim()) {
      toastError('El nombre del cliente es requerido')
      return
    }
    if (!email.trim()) {
      toastError('El email del cliente es requerido')
      return
    }

    setIsLoading(true)
    try {
      await apiClient.put(
        `/api/admin-protected/reservas/${reserva.id}/cliente`,
        {
          cliente_nombre: nombre,
          cliente_email: email,
          cliente_telefono: telefono,
          cliente_documento: numero_documento,
          cliente_tipo_documento: tipo_documento,
        }
      )

      toastSuccess('Datos del cliente actualizados exitosamente')

      // Refrescar datos de la reserva tanto en el detalle como en la lista
      if (reservaId) {
        // Invalidar el detalle específico
        queryClient.invalidateQueries({
          queryKey: ['reserva-detail', reservaId],
        })
        // Invalidar la lista completa
        queryClient.invalidateQueries({
          queryKey: ['reservas-admin'],
        })
      }

      onSuccess()
      onClose()
    } catch (error: any) {
      console.error('Error editando cliente:', error)
      toastError(error.response?.data?.detail || 'Error al editar datos del cliente')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div
        className="bg-white rounded-lg shadow-xl max-w-md w-full"
        style={{ borderTop: `4px solid ${colors.primary}` }}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            ✏️ Editar Datos del Cliente
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-6 space-y-4">
          {/* Reserva ID - Solo lectura */}
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              ID de Reserva
            </label>
            <input
              type="text"
              value={reserva.id}
              disabled
              className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
            />
            <p className="text-xs text-gray-500 mt-1">⚠️ Campo no editable (crítico)</p>
          </div>

          {/* Nombre */}
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              Nombre del Cliente *
            </label>
            <input
              type="text"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              placeholder="Nombre completo"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
              style={{ focusOutlineColor: colors.primary }}
            />
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              Email *
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="correo@ejemplo.com"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
              style={{ focusOutlineColor: colors.primary }}
            />
          </div>

          {/* Teléfono */}
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              Teléfono
            </label>
            <input
              type="tel"
              value={telefono}
              onChange={(e) => setTelefono(e.target.value)}
              placeholder="+56912345678"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
              style={{ focusOutlineColor: colors.primary }}
            />
          </div>

          {/* Tipo de Documento */}
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              Tipo de Documento
            </label>
            <select
              value={tipo_documento}
              onChange={(e) => setTipoDocumento(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
              style={{ focusOutlineColor: colors.primary }}
            >
              <option value="RUT">RUT</option>
              <option value="Pasaporte">Pasaporte</option>
              <option value="Cédula">Cédula</option>
              <option value="Otro">Otro</option>
            </select>
          </div>

          {/* Número de Documento */}
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              Número de Documento
            </label>
            <input
              type="text"
              value={numero_documento}
              onChange={(e) => setNumeroDocumento(e.target.value)}
              placeholder="12345678-9"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
              style={{ focusOutlineColor: colors.primary }}
            />
          </div>

          {/* Info */}
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-xs text-blue-700">
              ℹ️ Solo se pueden editar datos del cliente. Otros campos como fechas, espacios y monto son críticos y no se pueden modificar.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex gap-2 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={handleGuardar}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              backgroundColor: colors.primary,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = isLoading ? '0.5' : '0.9'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '1'
            }}
          >
            {isLoading ? '⏳ Guardando...' : '✓ Guardar Cambios'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default EditarClienteReservaModal
